import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { SEO } from "../components/shared/SEO";
import { PageHeader } from "../components/shared/PageHeader";
import { Reveal } from "../components/shared/Reveal";
import { ProcessFlow } from "../components/shared/Sections";
import { Field, PrivacyCheck, SuccessBox } from "../components/shared/Form";
import { OperationCard } from "../components/operations/OperationCard";
import { INVEST, IMAGES } from "../content/site";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const InvestorForm = () => {
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", interest: "", message: "", privacy: false });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { type: "investor", name: f.name, email: f.email, phone: f.phone, city: f.city, message: f.message, details: { interesse: f.interest }, privacy_accepted: f.privacy });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (sent) return <SuccessBox testId="investor-form-success" title="Grazie per l'interesse." text="Il team Home Haven ti ricontatterà per un confronto riservato sulle opportunità in corso." />;
  return (
    <form onSubmit={submit} data-testid="investor-form" className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <Field label="Nome e cognome" name="name" value={f.name} onChange={set} required testId="investor-name" />
      <Field label="Email" name="email" type="email" value={f.email} onChange={set} required testId="investor-email" />
      <Field label="Telefono" name="phone" type="tel" value={f.phone} onChange={set} testId="investor-phone" />
      <Field label="Città" name="city" value={f.city} onChange={set} testId="investor-city" />
      <div className="md:col-span-2"><Field label="Tipologia di interesse" name="interest" as="select" value={f.interest} onChange={set} options={["Partecipazione a singolo progetto", "Co-investimento su più operazioni", "Immobili a reddito", "Altro"]} testId="investor-interest" /></div>
      <div className="md:col-span-2"><Field label="Messaggio" name="message" as="textarea" value={f.message} onChange={set} placeholder="Raccontaci il tuo interesse…" testId="investor-message" /></div>
      <div className="md:col-span-2"><PrivacyCheck checked={f.privacy} onChange={(v) => set("privacy", v)} testId="investor-privacy" /></div>
      <div className="md:col-span-2"><button type="submit" disabled={busy} data-testid="investor-submit" className="btn-primary disabled:opacity-50">{busy ? "Invio…" : "Parla con Home Haven"}</button></div>
    </form>
  );
};

export default function Investitori() {
  const s = useSettings();
  const { data: opps = [] } = useQuery({ queryKey: ["operations"], queryFn: () => api.get("/operations").then((r) => r.data) });
  return (
    <>
      <SEO title="Investire in Progetti Immobiliari | Home Haven" description="Scopri il metodo Home Haven per strutturare operazioni immobiliari con investitori e partner, attraverso analisi, trasparenza e controllo del progetto." image={IMAGES.hero} />
      <PageHeader eyebrow={`Investi con Home Haven — ${INVEST.title}`} title="Investire in operazioni immobiliari strutturate" image={IMAGES.hero} text={INVEST.paragraphs[0]} />

      <section className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="lead">{INVEST.paragraphs[1]}</p>
            <p className="lead mt-6">{INVEST.paragraphs[2]}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9 flex flex-col gap-4">
            <Link to="/operazioni" data-testid="invest-cta-operazioni" className="btn-primary">Scopri le operazioni aperte</Link>
            <a href="#contatto-investitori" data-testid="invest-cta-parla" className="btn-outline">Parla con Home Haven</a>
          </Reveal>
        </div>
      </section>

      <section className="section bg-ink text-cream relative grain overflow-hidden">
        <div className="container-hh relative">
          <Reveal className="mb-16"><div className="eyebrow mb-6">Il processo</div><h2 className="h-section">Dall'opportunità all'exit.</h2></Reveal>
          <div className="border-y border-white/10 py-12"><ProcessFlow /></div>
        </div>
      </section>

      {opps.length > 0 && (
        <section className="section bg-white">
          <div className="container-hh">
            <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"><div><div className="eyebrow mb-6">Operazioni aperte</div><h2 className="h-section">Le operazioni disponibili.</h2></div><Link to="/operazioni" className="btn-link">Tutte le operazioni</Link></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">{opps.filter((o) => o.status !== "conclusa").slice(0, 2).map((o, i) => <OperationCard key={o.id} o={o} index={i} />)}</div>
          </div>
        </section>
      )}

      <section id="contatto-investitori" className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4"><div className="eyebrow mb-6">Contatto riservato</div><h2 className="h-section">Parla con Home Haven.</h2><p className="lead mt-6">Un confronto diretto per capire se e come collaborare su un progetto specifico.</p></Reveal>
          <div className="lg:col-span-7 lg:col-start-6"><InvestorForm /></div>
        </div>
      </section>

      <section className="bg-beige py-14">
        <div className="container-hh">
          <div data-testid="investor-disclaimer" className="border-l-2 border-gold pl-6 md:pl-8 max-w-4xl">
            <div className="eyebrow !text-ink/60 mb-3">Nota legale</div>
            <p className="text-sm font-light leading-relaxed text-ink/70">{s.investor_disclaimer}</p>
          </div>
        </div>
      </section>
    </>
  );
}
